import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {isFinite} from 'lodash';
import {
    AstView,
    convertCssUnit,
    CSSProperty,
    SizeUnit,
    EnrichedAstNode,
    SpacingStrategy,
    extractNumericUnitAndValue,
} from 'rendition-core';

import {Checkbox} from 'Components/Tailwind/Checkbox';
import {EditorSectionLargeTitle, EditorSectionTitle} from './EditingTitle';
import {Chevron} from 'Icons/Chevron';
import TabbedButtonToggle from 'Components/Tailwind/TabbedButtonToggle';
import {uniq} from 'lodash';
import {NumericTextField} from 'Components/Rendition/NumericTextField';
import {Separator} from 'Views/Preview/Builder/EditControls';
import {useViewContext} from 'Contexts/ViewContext';
import {useSelectedParentNode} from 'Hooks/AstHooks/useSelectedParentNode';

interface Props {}

const FIXED_UNIT_OPTIONS = ['px', 'em', 'rem'];

const PROPERTY_OPTS: {value: SpacingStrategy}[] = [
    {
        value: 'fill',
    },
    {value: 'fixed'},
    {value: 'hug'},
    {value: 'stretch'},
];

const useResetMinMax = (
    selectedNodes: EnrichedAstNode[],
    setAllowMin: (allow: boolean) => void,
    setAllowMax: (allow: boolean) => void,
    minKey: CSSProperty,
    maxKey: CSSProperty
) => {
    useEffect(() => {
        setAllowMin(selectedNodes.every((node) => !!node.styles[minKey]));
        setAllowMax(selectedNodes.every((node) => !!node.styles[maxKey]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedNodes]);
};

type Dimension = 'width' | 'height';

type SizeFit = EnrichedAstNode['calculatedLayoutAbstraction']['widthSpacingStrategy'];

export const SizeEditor: React.FC<Props> = () => {
    const {selectedNodes, view, selectedNode, setView} = useViewContext();

    const dimensions: Dimension[] = ['width', 'height'];

    const hasAspectRatioPreservedImage = useMemo(
        () =>
            selectedNodes.some(
                (n) =>
                    n.type === 'Image' &&
                    n.inputLayoutAbstraction?.widthSpacingStrategy === 'fill' &&
                    n.calculatedLayoutAbstraction.preserveAspectRatio
            ),
        [selectedNodes]
    );
    const hideMaxWidthAndHeight = hasAspectRatioPreservedImage;

    const [expandConstraints, setExpandConstraints] = useState(false);

    const showMinMaxControls = true;

    if (!selectedNode) {
        return <div>yikes</div>;
    }

    return (
        <div className="box-border flex flex-col gap-4">
            <EditorSectionLargeTitle title={'Size'} padding={''} />

            <div className="box-border flex flex-col gap-3 pl-2">
                <SizingControls property={'width'} />
                <Separator />

                {hideMaxWidthAndHeight === false && <SizingControls property={'height'} />}
                {showMinMaxControls && (
                    <>
                        <Separator />
                        <div className="flex flex-col gap-2">
                            <button
                                className="flex w-full items-center justify-between rounded px-4 hover:bg-white hover:bg-opacity-5"
                                onClick={() => setExpandConstraints(!expandConstraints)}
                            >
                                <EditorSectionTitle title={'Constraints'} />
                                <Chevron direction={expandConstraints ? 'up' : 'down'} color="#ffffff" />
                            </button>
                            {expandConstraints && (
                                <div className="flex flex-col gap-3 rounded bg-white bg-opacity-5 p-2">
                                    {dimensions.map((property) =>
                                        // Hide when height is selected and there is an image with aspect ratio preserved
                                        property === 'height' && hideMaxWidthAndHeight ? null : (
                                            <Constraints
                                                key={'constraints' + property}
                                                selectedNodes={selectedNodes}
                                                view={view}
                                                setView={setView}
                                                hideMaxWidthAndHeight={hideMaxWidthAndHeight}
                                                property={property}
                                                sizeFit={
                                                    selectedNode?.calculatedLayoutAbstraction[
                                                        property + 'SpacingStrategy'
                                                    ]
                                                }
                                            />
                                        )
                                    )}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

interface SizingControlsProps {
    property: Dimension;
}

const SizingControls: React.FC<SizingControlsProps> = ({property}) => {
    const {updateSelectedNodes, selectedNode, selectedNodes} = useViewContext();

    const {parentNode} = useSelectedParentNode();

    const numericValue = extractNumericUnitAndValue(selectedNode!.styles[property]);
    const sizeFit = selectedNode?.calculatedLayoutAbstraction[property + 'SpacingStrategy'];

    const allowStretchHeight = parentNode?.calculatedLayoutAbstraction.flexDirection === 'row' && property === 'height';
    const allowStretchWidth =
        parentNode?.calculatedLayoutAbstraction.flexDirection === 'column' && property === 'width';

    const showStrechOption = allowStretchWidth || allowStretchHeight;

    const handlePropertyChange = useCallback<(value: number) => void>(
        (value) => {
            updateSelectedNodes((node) => {
                if (property === 'width') {
                    node.styles.width = value + numericValue.unit;
                    node.inputLayoutAbstraction.width = value + numericValue.unit;
                } else {
                    node.styles.height = value + numericValue.unit;
                    node.inputLayoutAbstraction.height = value + numericValue.unit;
                }
            });
        },
        [property, updateSelectedNodes, numericValue.unit]
    );

    const hasAspectRatioPreservedImage = useMemo(
        () =>
            selectedNodes.some(
                (n) =>
                    n.type === 'Image' &&
                    n.inputLayoutAbstraction?.widthSpacingStrategy === 'fill' &&
                    n.calculatedLayoutAbstraction.preserveAspectRatio
            ),
        [selectedNodes]
    );
    const updateSizeFit = useCallback(
        (sizeFit: SizeFit) => {
            updateSelectedNodes((node) => {
                node.inputLayoutAbstraction[property + 'SpacingStrategy'] = sizeFit;
            });
        },
        [updateSelectedNodes, property]
    );

    const updateUnits = useCallback(
        (unit: SizeUnit) => {
            updateSelectedNodes((node) => {
                delete node.inputLayoutAbstraction[property];
                if (!node.inputLayoutAbstraction.units) {
                    node.inputLayoutAbstraction.units = {};
                }
                if (FIXED_UNIT_OPTIONS.includes(unit)) {
                    node.inputLayoutAbstraction[property + 'SpacingStrategy'] = 'fixed';
                } else {
                    node.inputLayoutAbstraction[property + 'SpacingStrategy'] = 'fill';
                }
                node.inputLayoutAbstraction.units[property] = unit;
            });
        },
        [updateSelectedNodes, property]
    );

    // TODO Improve the UI for this instead of disappearing.
    if (property === 'height' && hasAspectRatioPreservedImage) {
        return null;
    }

    return (
        <div className="flex w-full flex-col items-start justify-center">
            <h1 className="flex items-start text-start text-sm font-extrabold capitalize text-white">{property}</h1>
            <div className="flex w-full items-center justify-center gap-2">
                <div className="w-18 flex flex-col">
                    <div className="w-14">
                        {(sizeFit === 'fixed' || sizeFit === 'fill') && (
                            <NumericTextField
                                unit={numericValue.unit}
                                onUnitChange={updateUnits}
                                onValueChange={handlePropertyChange}
                                value={numericValue.value}
                                inputType={'underline'}
                            />
                        )}
                    </div>
                </div>

                <TabbedButtonToggle
                    items={PROPERTY_OPTS.filter((opt) => opt.value !== 'stretch' || showStrechOption)}
                    classNames="w-full"
                    onClick={updateSizeFit}
                    value={sizeFit === 'Mixed' ? undefined : sizeFit}
                />
            </div>
        </div>
    );
};

interface ConstraintsProps {
    selectedNodes: EnrichedAstNode[];
    view: AstView;
    setView: (view: AstView) => void;
    hideMaxWidthAndHeight: boolean;
    property: Dimension;
    sizeFit: SizeFit | undefined;
}

const Constraints: React.FC<ConstraintsProps> = ({
    selectedNodes,
    view,
    setView,
    hideMaxWidthAndHeight,
    property,
    sizeFit,
}) => {
    const minKey = property === 'width' ? 'minWidth' : 'minHeight';
    const maxKey = property === 'width' ? 'maxWidth' : 'maxHeight';

    const [allowMin, setAllowMin] = React.useState(() => selectedNodes.every((node) => !!node.styles[minKey]));
    const [allowMax, setAllowMax] = React.useState(() => selectedNodes.every((node) => !!node.styles[maxKey]));

    const minEquals = useMemo(() => {
        const values = selectedNodes.map((node) => node.styles[minKey]);
        return uniq(values).length === 1;
    }, [minKey, selectedNodes]);

    const maxEquals = useMemo(() => {
        const values = selectedNodes.map((node) => node.styles[maxKey]);
        return uniq(values).length === 1;
    }, [maxKey, selectedNodes]);

    const minUnits = minEquals ? selectedNodes[0].calculatedLayoutAbstraction.units?.[minKey] || 'px' : 'px';
    const maxUnits = maxEquals ? selectedNodes[0].calculatedLayoutAbstraction.units?.[maxKey] || 'px' : 'px';

    useResetMinMax(selectedNodes, setAllowMin, setAllowMax, minKey, maxKey);

    const onMinPropertyAllowChange = useCallback(
        (checked: boolean) => {
            setAllowMin(checked);
            selectedNodes.forEach((node) => {
                if (node.meta.systemGenerated) {
                    node.meta.systemGenerated = false;
                }
                if (checked) {
                    node.styles[minKey] = node.position[property] + 'px';
                    convertCssUnit({
                        key: minKey,
                        nodeId: node.internalId,
                        view,
                        toUnit: node.inputLayoutAbstraction.units?.[minKey] || 'px',
                        assignNewValueToNode: true,
                    });
                } else {
                    selectedNodes.forEach((node) => {
                        delete node.styles[minKey];
                    });
                }
            });

            setView({...view});
        },
        [setAllowMin, selectedNodes, setView, view, minKey, property]
    );

    const onMaxPropertyAllowChange = useCallback(
        (checked: boolean) => {
            if (checked) {
                selectedNodes.forEach((node) => {
                    if (maxKey === 'maxWidth') {
                        node.inputLayoutAbstraction.maxWidth = true;
                    }
                    setAllowMax(true);
                });
            } else {
                setAllowMax(false);
                selectedNodes.forEach((node) => {
                    if (maxKey === 'maxWidth') {
                        node.inputLayoutAbstraction.maxWidth = false;
                    }
                    delete node.styles[maxKey];
                });
            }
            setView({...view});
        },
        [maxKey, selectedNodes, setAllowMax, setView, view]
    );

    const handleMaxChange = useCallback<(value: number) => void>(
        (value: number) => {
            if (isFinite(value)) {
                selectedNodes.forEach((node) => {
                    node.styles[maxKey] = `${value}${maxUnits}`;
                });

                setView({...view});
            }
        },
        [maxKey, maxUnits, selectedNodes, setView, view]
    );

    const handleMinChange = useCallback<(value: number) => void>(
        (value: number) => {
            if (isFinite(value)) {
                selectedNodes.forEach((node) => {
                    node.styles[minKey] = `${value}${minUnits}`;
                });

                setView({...view});
            }
        },
        [minKey, minUnits, selectedNodes, setView, view]
    );

    return (
        <div className="flex w-full flex-col items-center justify-start gap-4">
            {(sizeFit === 'fixed' || sizeFit === 'fill') && (
                <div className="flex w-full flex-col items-start justify-start gap-2">
                    <div className="text-sm font-extrabold capitalize text-white">{property}</div>
                    <div className="flex w-full flex-row items-center justify-between gap-2">
                        <div className="w-14">
                            <Checkbox label="min" checked={allowMin} onChange={onMinPropertyAllowChange} />
                        </div>
                        <div className="w-full">
                            <NumericTextField
                                label="Min"
                                unit={minUnits}
                                onValueChange={handleMinChange}
                                value={selectedNodes[0].styles[minKey] as number}
                                disabled={!allowMin}
                                inputType={'underline'}
                            />
                        </div>
                    </div>
                    {!hideMaxWidthAndHeight && property !== 'height' && (
                        <div className="flex h-8 w-full flex-row items-center justify-between gap-2">
                            <div className="w-14">
                                <Checkbox label="max" checked={allowMax} onChange={onMaxPropertyAllowChange} />
                            </div>
                            <div className="w-full">
                                <NumericTextField
                                    label="Max"
                                    unit={maxUnits}
                                    onValueChange={handleMaxChange}
                                    value={selectedNodes[0].styles[maxKey] as number}
                                    disabled={!allowMax}
                                    inputType={'underline'}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
